import type { DefaultOptionType } from "antd/es/select";
import { clsx } from "clsx";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { type TUserRoles } from "../app/app.constants";
import type { AppRoute } from "../types/global.types";
import { capitalize } from "./string.utils";

// Add a unique "key" field to each item in an array (for tables/lists)
export const addKeyInArray = (data: any) => {
  return data?.map(
    (el: object & { id?: string; _id?: string }, idx: number) => ({
      ...el,
      key: el?.id || el?._id || `${idx}`,
    })
  );
};

// Check if the option label contains the search input (case-insensitive)
export const searchInLabel = (
  input: string,
  option?: { label: string; value: string | number }
) => {
  return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
};

export const selectStringOption = (
  data: string[],
  option?: "number" | "string" | "boolean",
  isCapitalLabel?: boolean
) => {
  return data.map((el) => ({
    label: isCapitalLabel ? capitalize(el) : el,
    value: option === "number" ? Number(el) : el,
  }));
};

// Generate a "back" link path by removing parts from the end of a URL path
export const generateBackLink = (str: string, backCount: number = 1) => {
  const pathArray = str.split("/");
  return pathArray.slice(0, pathArray.length - backCount).join("/");
};

// Validate if a given string is a valid URL (for AntD form validation)
export const validateUrl = async (_: any, value: string) => {
  if (!value) {
    return Promise.resolve();
  }

  const urlRegex =
    /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/.*)?$/;

  if (!urlRegex.test(value)) {
    return Promise.reject(new Error("Please enter a valid URL"));
  }
};

// Calculate skip (offset) value for pagination
export const getSkipData = (page: number, limit: number) => {
  return limit * (page - 1);
};

export const countParPageSerialNumber = (
  page: number,
  limit: number,
  index: number
) => {
  return index + 1 + (page - 1) * limit;
};

export function cn(...inputs: any[]) {
  return twMerge(clsx(inputs));
}

export const filterRoutesByRole = (
  routes: AppRoute[],
  role: TUserRoles,
  labelFilter = false
): AppRoute[] => {
  return routes
    .filter(
      (route) =>
        route.roles.includes(role) && (labelFilter ? route.label : true)
    )
    .map((route) => ({
      ...route,
      children: route.children
        ? filterRoutesByRole(route.children, role, labelFilter)
        : undefined,
    }));
};

/**
 * Flattens nested routes into a single array
 */
export const flattenRoutes = (routes: AppRoute[]): AppRoute[] => {
  return routes.reduce<AppRoute[]>((acc, route) => {
    acc.push({ ...route, children: undefined }); // push parent
    if (route.children) {
      acc.push(...flattenRoutes(route.children)); // push children recursively
    }
    return acc;
  }, []);
};

export const findMenuKeys = (
  routes: AppRoute[],
  path: string
): { selectedKey: string; openKeys: string[] } => {
  for (const route of routes) {
    // Exact or startsWith match for the current path
    if (path === route.path || path.startsWith(route.path + "/")) {
      if (!route.children) {
        return { selectedKey: route.path, openKeys: [] };
      }

      // Recursively find inside children
      const childResult = findMenuKeys(route.children, path);
      if (childResult.selectedKey) {
        return {
          selectedKey: childResult.selectedKey,
          openKeys: [route.path, ...childResult.openKeys],
        };
      }

      return { selectedKey: route.path, openKeys: [] };
    }

    // Recurse for deeper nested routes
    if (route.children) {
      const childResult = findMenuKeys(route.children, path);
      if (childResult.selectedKey) {
        return {
          selectedKey: childResult.selectedKey,
          openKeys: [route.path, ...childResult.openKeys],
        };
      }
    }
  }

  return { selectedKey: "", openKeys: [] };
};

// Generates a base path dynamically based on the user's role.
// export const generatePath = (
//   role: TUserRoles | undefined,
//   path: string,
//   id?: string | number,
//   extraPath?: string
// ): string => {
//   const { SUPER_ADMIN, ADMIN } = USER_ROLES;
//   const base = role === SUPER_ADMIN || role === ADMIN ? "/admin" : "/user";

//   return `${base}/${path}${id ? `/${id}` : ""}${
//     extraPath ? `/${extraPath}` : ""
//   }`;
// };

// export const normalizeValue = (val: unknown): string | number => {
//   if (val === null || val === undefined) return 'n/a';

//   if (typeof val === 'number') return isNaN(val) ? 0 : val;

//   if (typeof val === 'string') {
//     const trimmed = val.trim();
//     return trimmed === '' ? 'n/a' : capitalize(trimmed);
//   }

//   // For objects or arrays, return a string summary or "n/a"
//   if (typeof val === 'object') {
//     return Array.isArray(val)
//       ? val.length > 0
//         ? val.join(', ')
//         : 'n/a'
//       : 'n/a';
//   }

//   return 'n/a';
// };

export const normalizeValue = (
  val: unknown,
  defaultValue: string | number = "N/A"
): string | number => {
  // Handle null or undefined
  if (val === null || val === undefined) return defaultValue;

  // Handle numbers — always return 0 if NaN
  if (typeof val === "number") return isNaN(val) ? 0 : val;

  // Handle strings — trim, capitalize, or return default
  if (typeof val === "string") {
    const trimmed = val.trim();
    return trimmed === "" ? defaultValue : capitalize(trimmed);
  }

  // Handle arrays — join or fallback
  if (Array.isArray(val)) {
    return val.length > 0 ? val.join(", ") : defaultValue;
  }

  // Handle objects — optional summary or fallback
  if (typeof val === "object") {
    const entries = Object.entries(val as Record<string, unknown>);
    return entries.length > 0
      ? `${entries[0][0]}: ${entries[0][1]}`
      : defaultValue;
  }

  return defaultValue;
};

// utils/addSerial.ts
export const addUniqueKey = <T extends Record<string, any>>(
  data: T[],
  start = 1
): (T & { sl: number; key: string })[] => {
  return data.map((item, index) => ({
    ...item,
    sl: start + index,
    key: item._id || `${start + index}-${Date.now()}-${Math.random()}`,
  }));
};

export const selectArrayOption = (
  data: any[],
  label: string,
  value: string | number,
  isCapitalLabel?: boolean
) => {
  return data?.map((el) => ({
    label: isCapitalLabel ? capitalize(el[label]) : el[label],
    value: typeof value === "number" ? Number(el[value]) : el[value] || null,
  }));
};

export const addCommaInNumber = (number: number | string) => {
  return number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export const filterOptions = (
  options: ({
    label: string;
    value: string | number;
    icon?: ReactNode;
  } & DefaultOptionType)[],
  searchTerm: string
): ({
  label: string;
  value: string | number;
  icon?: ReactNode;
} & DefaultOptionType)[] => {
  if (!searchTerm) return options;

  const lowerSearch = searchTerm.toLowerCase();

  return options.filter((item) =>
    item.label?.toString().toLowerCase().includes(lowerSearch)
  );
};
